import { CornerGuitar } from '../CornerGuitar.tsx';
import { PillButton } from '../PillButton.tsx';
import { LineupNames } from '../LineupNames.tsx';
import { EYEBROW } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { getArtistGroups } from '../../application/festivalSelectors.ts';
import { cdnSrcSet, cdnVariant, cdnHeroUrl, cdnAspectUrl } from '../../lib/cdn.ts';
import type { HeroCta, PageHero, PageImage } from '../../lib/types.ts';

/** The v2 hero headline: 44px on phones, 72px from md up, set tight. */
const HERO_H1 = 'font-display text-[44px] md:text-[72px] leading-[.92] tracking-[-0.03em]';

/**
 * Full-bleed hero background <picture>. The BackOffice generates a focus-point-aware crop per
 * aspect; to show that crop (not a center slice) we request the dedicated 5:1 hero rendition on
 * desktop (cdnHeroUrl — the hero band is close to 5:1 at desktop widths, so object-cover barely
 * re-crops) and a taller 3:2 crop on phones (cdnAspectUrl) so the hero is not a thin sliver. This
 * only swaps which rendition fills the box — the box itself is unchanged/content-driven.
 * Legacy/non-media URLs degrade to a plain srcset on the original. `sizes` is always 100vw (the
 * hero spans the viewport).
 */
function HeroPicture({ image }: { image: PageImage }) {
  const desktop = cdnHeroUrl(image.src);
  const mobile = cdnAspectUrl(image.src, '3x2');
  const desktopSrcSet = cdnSrcSet(desktop);
  const mobileSrcSet = cdnSrcSet(mobile);
  const fallbackSrc = mobileSrcSet ? cdnVariant(mobile, 'mobile') : image.src;

  return (
    <picture>
      {desktopSrcSet && <source media="(min-width: 768px)" srcSet={desktopSrcSet} sizes="100vw" />}
      <img
        src={fallbackSrc}
        srcSet={mobileSrcSet ?? undefined}
        sizes="100vw"
        alt={image.alt}
        className="absolute inset-0 w-full h-full object-cover"
      />
    </picture>
  );
}

/**
 * Hero for CMS-driven pages. Three designs (CMS-044):
 *
 * - 'enkel' (default): title + optional subtitle, no image — matches the hero look from
 *   before this field existed (an optional guitar corner is still honored, unchanged).
 * - 'plakat': a poster — the announced line-up rendered as a tiered backslash list
 *   (headliner names large, "Midtfynske oplevelser" smaller below), optionally over a
 *   background image tinted with the brand yellow so the green names stay readable.
 * - 'foto': full-bleed image on the near-black `greenHero`, which the image fades into at the
 *   bottom so the hero hands off to the first section instead of ending on a hard edge.
 *
 * CTAs render as pill buttons under the copy on every design: coral for the primary, a quiet
 * outline for the secondary (in the tone that suits the ground it sits on).
 *
 * `headingPrefix` puts the festival's name into the page's <h1> without putting it on the screen.
 * The front page's headline is a campaign line — "Vi gør klar til at annoncere de første navne" is
 * not what a page about Midtfyns Festival should be called to a screen reader or a search engine —
 * and a subpage's is just "Program". Rendered as a screen-reader-only span *inside* the same <h1>,
 * so the page keeps exactly one heading, the design is untouched, and nothing is hidden that is
 * not also said.
 */
export interface CmsHeroProps extends PageHero {
  /** See the note above. Null when the title already names the festival. */
  readonly headingPrefix?: string | null;
}

export function CmsHero(props: CmsHeroProps) {
  const { title, subtitle, image, guitar, design = 'enkel', primaryCta, secondaryCta, headingPrefix } = props;
  const hasCtas = !!primaryCta || !!secondaryCta;

  if (design === 'plakat') {
    return <PlakatHero {...props} />;
  }

  if (design === 'foto' && image) {
    return (
      <section className="relative bg-mf-greenHero overflow-hidden pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <HeroPicture image={image} />
          <div className="absolute inset-0 bg-black/30" />
          {/* Fades the photo into the hero own ground rather than cutting it off. */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-mf-greenHero to-transparent" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
          <h1 className={`${HERO_H1} text-mf-yellow`}>
            {headingPrefix && <span className="sr-only">{headingPrefix} – </span>}{title}
          </h1>
          {subtitle && (
            <p className="mt-6 md:mt-8 text-mf-yellow/85 text-lg md:text-xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {hasCtas && <HeroCtas primaryCta={primaryCta} secondaryCta={secondaryCta} outlineVariant="outlineLight" />}
        </div>
      </section>
    );
  }

  const showGuitar = !image && !!guitar;

  return (
    <section className="relative bg-mf-yellow overflow-hidden pt-10 md:pt-14 pb-12 md:pb-16">
      {image ? (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <HeroPicture image={image} />
          <div className="absolute inset-0 bg-mf-yellow/35" />
        </div>
      ) : showGuitar ? (
        <CornerGuitar corner={guitar ?? 'bottom-right'} colorClass="text-mf-green" opacityClass="opacity-[0.08]" />
      ) : null}

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
        <h1 className={`${HERO_H1} text-mf-black`}>
          {headingPrefix && <span className="sr-only">{headingPrefix} – </span>}{title}
        </h1>
        {subtitle && (
          <p className="mt-6 md:mt-8 text-mf-black/80 text-lg md:text-xl leading-relaxed">
            {subtitle}
          </p>
        )}
        {hasCtas && <HeroCtas primaryCta={primaryCta} secondaryCta={secondaryCta} />}
      </div>
    </section>
  );
}

/**
 * 'plakat' design: the line-up as the hero. Pulls announced artists + features from the
 * published festival data (DataContext) and renders them as a tiered backslash list. The
 * authored title is kept as an sr-only <h1> (the names are the visual headline); an optional
 * background image sits behind, tinted with brand yellow so the green names stay legible.
 */
function PlakatHero({ title, subtitle, image, guitar, primaryCta, secondaryCta, headingPrefix }: CmsHeroProps) {
  const { data } = useFestivalData();
  const groups = data ? getArtistGroups(data) : null;
  const artists = groups?.artists ?? [];
  const features = groups?.features ?? [];
  const hasCtas = !!primaryCta || !!secondaryCta;

  return (
    <section className="relative bg-mf-yellow overflow-hidden density-pad">
      {image ? (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <HeroPicture image={image} />
          <div className="absolute inset-0 bg-mf-yellow/45" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-mf-yellow to-transparent" />
        </div>
      ) : (
        <CornerGuitar corner={guitar ?? 'bottom-right'} colorClass="text-mf-green" opacityClass="opacity-[0.08]" />
      )}

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
        <h1 className="sr-only">{headingPrefix ? headingPrefix + " – " : ""}{title}</h1>
        {subtitle && (
          <div className={`${EYEBROW} text-mf-coralInk mb-5 md:mb-6`}>
            {subtitle}
          </div>
        )}

        {artists.length > 0 && (
          <LineupNames
            items={artists.map(a => ({ name: a.name, id: a.id }))}
            className="font-display text-mf-black text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          />
        )}

        {features.length > 0 && (
          <div className={artists.length > 0 ? 'mt-8 pt-6 border-t border-mf-green/20' : ''}>
            <LineupNames
              items={features.map(a => ({ name: a.name, id: a.id }))}
              className="font-display text-mf-black/85 text-xl md:text-2xl lg:text-3xl leading-[1.15] tracking-tight"
            />
          </div>
        )}

        {hasCtas && <HeroCtas primaryCta={primaryCta} secondaryCta={secondaryCta} />}
      </div>
    </section>
  );
}

function HeroCtas({
  primaryCta, secondaryCta, outlineVariant = 'outline',
}: {
  primaryCta?: HeroCta;
  secondaryCta?: HeroCta;
  /** 'outlineLight' on the 'foto' design — a dark-ink outline is nearly invisible on the
   *  dark photo overlay. */
  outlineVariant?: 'outline' | 'outlineLight';
}) {
  return (
    <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
      {primaryCta && (
        <PillButton as="a" href={primaryCta.link} variant="coral">
          {primaryCta.label}
        </PillButton>
      )}
      {secondaryCta && (
        <PillButton as="a" href={secondaryCta.link} variant={outlineVariant}>
          {secondaryCta.label}
        </PillButton>
      )}
    </div>
  );
}
